import React, { useEffect, useRef } from 'react';
import { useKeyboardShortcuts } from '@/context/KeyboardShortcuts';
import styles from './index.less';

export type ShortcutHintPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

type Props = {
  /** The key or sequence that triggers this shortcut, e.g. 'n' or ['g','l']. */
  shortcutKey: string | [string, string];
  /** Human-readable label registered with the shortcut system. */
  label: string;
  /** Action fired when the shortcut key is pressed. Omit when noRegister is true. */
  onTrigger?: () => void;
  children: React.ReactNode;
  /** Where to position the hint badge relative to the element. Default: top-right. */
  position?: ShortcutHintPosition;
  /** Override the auto-generated id (key string). Required when the same key is used twice on a page. */
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Only show the hint badge; do not register the shortcut (use when it is already registered elsewhere). */
  noRegister?: boolean;
  /**
   * Render the badge inline (flex row) instead of absolutely positioned.
   * Use this inside containers with overflow:hidden (e.g. sidebar nav items)
   * so the badge is never clipped by an ancestor.
   */
  inline?: boolean;
};

const positionClass: Record<ShortcutHintPosition, string> = {
  'top-right': styles.topRight,
  'top-left': styles.topLeft,
  'bottom-right': styles.bottomRight,
  'bottom-left': styles.bottomLeft,
};

function matchesFilter(shortcutKey: string | [string, string], hintFilter: string | null): boolean {
  if (hintFilter === null) return true;
  if (Array.isArray(shortcutKey)) return shortcutKey[0].toLowerCase() === hintFilter;
  return shortcutKey.toLowerCase() === hintFilter;
}

function BadgeContent({ shortcutKey, hintFilter, hintFilter2 }: { shortcutKey: string | [string, string]; hintFilter: string | null; hintFilter2: string | null }) {
  if (!Array.isArray(shortcutKey)) {
    return hintFilter ? <><mark className={styles.keyHighlight}>{shortcutKey}</mark></> : <>{shortcutKey}</>;
  }
  const [first, second] = shortcutKey;
  if (hintFilter2) {
    return <><mark className={styles.keyHighlight}>{first}</mark>{' → '}<mark className={styles.keyHighlight}>{second}</mark></>;
  }
  if (hintFilter) {
    return <><mark className={styles.keyHighlight}>{first}</mark>{' → '}{second}</>;
  }
  return <>{first}{' → '}{second}</>;
}

const ShortcutHint: React.FC<Props> = ({
  shortcutKey,
  label,
  onTrigger,
  children,
  position = 'top-right',
  id,
  className,
  style,
  noRegister = false,
  inline = false,
}) => {
  const { isHintMode, hintFilter, hintFilter2, registerShortcut, unregisterShortcut } = useKeyboardShortcuts();
  const onTriggerRef = useRef(onTrigger);
  onTriggerRef.current = onTrigger;

  const shortcutId = id ?? (Array.isArray(shortcutKey) ? shortcutKey.join('-') : shortcutKey);

  useEffect(() => {
    if (noRegister) return;
    registerShortcut({
      id: shortcutId,
      key: shortcutKey,
      label,
      action: () => onTriggerRef.current?.(),
    });
    return () => unregisterShortcut(shortcutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noRegister, shortcutId, JSON.stringify(shortcutKey), label, registerShortcut, unregisterShortcut]);

  const visible = isHintMode && matchesFilter(shortcutKey, hintFilter);

  if (inline) {
    return (
      <span className={`${styles.rootInline}${className ? ` ${className}` : ''}`} style={style}>
        <span className={styles.inlineContent}>{children}</span>
        {visible && (
          <kbd className={styles.badgeInline}>
            <BadgeContent shortcutKey={shortcutKey} hintFilter={hintFilter} hintFilter2={hintFilter2} />
          </kbd>
        )}
      </span>
    );
  }

  return (
    <span className={`${styles.root}${className ? ` ${className}` : ''}`} style={style}>
      {children}
      {visible && (
        <kbd className={`${styles.badge} ${positionClass[position]}`}>
          <BadgeContent shortcutKey={shortcutKey} hintFilter={hintFilter} hintFilter2={hintFilter2} />
        </kbd>
      )}
    </span>
  );
};

export default ShortcutHint;
